import { useEffect, useRef, useState } from "react";

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
const windCache = new Map();
const LS_KEY = "the99.cache.wind";

function readPersistedWind() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persistWind(value) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(value)); } catch {}
}

function getCacheKey(lat, lng) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

function degreesToCompass(degrees) {
  if (typeof degrees !== "number" || Number.isNaN(degrees)) return "—";
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
}

function formatUpdatedAt(date) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export default function useWind(location, { isOffline = false } = {}) {
  const [data, setData] = useState({
    label: "Loading…",
    status: "warn",
    speedKnots: null,
    direction: null,
    updatedAt: null,
    updatedLabel: null,
    error: null,
    loading: false,
  });
  const [refreshToggle, setRefreshToggle] = useState(0);
  const forceRefreshRef = useRef(false);

  const refresh = () => {
    forceRefreshRef.current = true;
    setRefreshToggle((c) => c + 1);
  };

  useEffect(() => {
    if (!location?.lat || !location?.lng) {
      setData({
        label: "GPS needed",
        status: "warn",
        speedKnots: null,
        direction: null,
        updatedAt: null,
        updatedLabel: null,
        error: "GPS needed",
        loading: false,
      });
      return;
    }

    const fetchWind = async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const cacheKey = getCacheKey(location.lat, location.lng);
      const cached = windCache.get(cacheKey);

      setData((prev) => ({ ...prev, loading: true }));

      // If offline, return persisted value immediately
      if (isOffline || !navigator.onLine) {
        const persisted = readPersistedWind();
        if (persisted) {
          setData({ ...persisted, loading: false });
        } else {
          setData({ label: "Offline", status: "warn", speedKnots: null, direction: null, updatedAt: null, updatedLabel: null, error: "Offline", loading: false });
        }
        return;
      }

      if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setData(cached.value);
        return;
      }

      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lng}&current=wind_speed_10m,wind_direction_10m&wind_speed_unit=kn`
        );

        if (!response.ok) {
          throw new Error("Wind API request failed");
        }

        const json = await response.json();
        const speedKnotsRaw = json?.current?.wind_speed_10m;
        const directionRaw = json?.current?.wind_direction_10m;

        if (typeof speedKnotsRaw !== "number" || typeof directionRaw !== "number") {
          throw new Error("Wind data unavailable");
        }

        const speedKnots = Math.round(speedKnotsRaw);
        const direction = degreesToCompass(directionRaw);
        const updatedAt = new Date();
        const value = {
          label: `${direction} ${speedKnots} knots`,
          status: "good",
          speedKnots,
          direction,
          updatedAt,
          updatedLabel: formatUpdatedAt(updatedAt),
          error: null,
          loading: false,
        };

        windCache.set(cacheKey, {
          value,
          timestamp: Date.now(),
        });
        persistWind(value);
        setData(value);
      } catch (error) {
        console.error("Wind fetch failed:", error);
        setData({
          label: "Unavailable",
          status: "warn",
          speedKnots: null,
          direction: null,
          updatedAt: null,
          updatedLabel: null,
          error: "Unavailable",
          loading: false,
        });
      }
    };

    fetchWind();
  }, [location?.lat, location?.lng, refreshToggle, isOffline]);

  return { ...data, refresh };
}
